import React, { useEffect, useCallback, useState } from 'react';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import styled from 'styled-components';

export interface PercentageFieldProps {
  initialValue: number | null;
  disabled?: boolean;
  error?: boolean;
  focusOnMount?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  onBlur: (v: PercentageFieldProps['initialValue']) => unknown;
}

/* eslint-disable max-len -- inline SVG path data */
const percentageIcon = `<svg
    width="14"
    height="13"
    viewBox="0 0 14 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.8438 1.15625C12.6562 0.96875 12.3125 0.96875 12.125 1.15625L1.125 12.1562C0.9375 12.3438 0.9375 12.6875 1.125 12.875C1.21875 12.9688 1.34375 13 1.5 13C1.625 13 1.75 12.9688 1.84375 12.875L12.8438 1.875C13.0312 1.6875 13.0312 1.34375 12.8438 1.15625ZM3 5C4.09375 5 5 4.125 5 3C5 1.90625 4.09375 1.03125 3 1.03125C1.875 1.03125 1 1.90625 1 3C1 4.0625 1.875 5 3 5ZM3 2C3.53125 2 4 2.46875 4 3C4 3.5625 3.53125 4 3 4C2.4375 4 2 3.5625 2 3C2 2.46875 2.4375 2 3 2ZM11 9C9.875 9 9 9.90625 9 11C9 12.125 9.875 13 11 13C12.0938 13 13 12.125 13 11C13 9.90625 12.0938 9 11 9ZM11 12C10.4375 12 10 11.5625 10 11C10 10.4688 10.4375 10 11 10C11.5312 10 12 10.4688 12 11C12 11.5625 11.5312 12 11 12Z"
      fill="%230073A3"
    />
  </svg>
`;
/* eslint-enable max-len */

const StyledInput = styled.input`
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 12px;
  padding-right: 40px;
  text-align: right;
  background-color: ${COLORS.whiteBase};
  border: 1px solid ${({ error }: { error: PercentageFieldProps['error'] }) =>
    error ? COLORS.critical : COLORS.greyBase};
  border-radius: 4px;
  background-image: url('data:image/svg+xml;utf8,${percentageIcon}');
  background-position: right 16px top 14px;
  background-repeat: no-repeat;
  &:disabled {
    background-color: ${COLORS.disabledBackground}
  }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
  }
  appearance: textfield;
`;

let onChangeTimeout: NodeJS.Timeout | undefined = undefined;

const PercentageField: React.FC<PercentageFieldProps> = ({
  disabled,
  error,
  inputRef,
  initialValue,
  focusOnMount,
  onBlur,
}) => {
  const [percentage, setPercentage] = useState<number | null>(initialValue);

  useEffect(() => {
    setPercentage(initialValue);
  }, [initialValue, setPercentage]);

  const handleBlur = useCallback(() => {
    onBlur(percentage);
  }, [percentage]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === '') {
        return setPercentage(null);
      }
      if (value == '00') {
        return setPercentage(0);
      }
      const percentage = parseInt(value, 10);
      if (percentage >= 0 && percentage <= 100) {
        setPercentage(percentage);
        clearTimeout(onChangeTimeout);
        onChangeTimeout = setTimeout(() => onBlur(percentage), 1000);
      }
    },
    [setPercentage, onBlur],
  );

  return (
    <StyledInput
      error={error}
      ref={inputRef}
      disabled={disabled}
      autoFocus={focusOnMount}
      value={percentage === null ? '' : percentage}
      onChange={handleChange}
      onBlur={handleBlur}
      type="number"
    />
  );
};

export default PercentageField;
